import React, { useState, useMemo, useCallback } from 'react'
import { Link } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import MonthPicker from '@/components/calendar/MonthPicker'
import OccupancyGrid from '@/components/calendar/OccupancyGrid'
import OccupancyCalendarGrid from '@/components/calendar/OccupancyCalendarGrid'
import OccupancyTimelineGrid from '@/components/calendar/OccupancyTimelineGrid'
import ViewSwitcher from '@/components/calendar/ViewSwitcher'
import SingleSelectDropdown from '@/components/calendar/SingleSelectDropdown'
import DayDetailModal from '@/components/calendar/DayDetailModal'
import CalendarUpdateBanner from '@/components/calendar/CalendarUpdateBanner'
import DeferredScopeNote from '@/components/groups/DeferredScopeNote'
import { cn } from '@/lib/utils'
import { FigureScopeContext } from '@/lib/figure-scope'
import { todayIso } from '@/lib/calendar-utils'
import { buildRoomTypeRowMeta, roomTypeHotelExtras } from '@/lib/room-type-tree'
import { spansHotels, groupHotelCount, isDeferredScope } from '@/lib/reporting-scope'
import { useCalendarNav } from '@/hooks/useCalendarNav'
import { TrendingUp, TrendingDown, Users, BarChart3, type LucideIcon } from 'lucide-react'
import { formatPickupRooms } from '@/lib/plain-language'
import type { SharedProps } from '@/types/shared'
import type { DayInfo, OccupancyDay, RateData, RoomType, RatePlan, RateHistoryEntry, ViewMode, AdrDay, RoomOccupancyEntry } from '@/types/calendar'

interface OccupancyProps extends SharedProps {
  month: number
  year: number
  totalRooms: number
  /** "Building" at a combined inventory of two or more listings; null keeps the pooled chip's "Hotel". */
  pooledOccupancyLabel?: string | null
  /** "Building ADR" at a combined inventory of two or more listings; null keeps "ADR". */
  adrLabel?: string | null
  occupancyData: OccupancyDay[]
  roomTypes: RoomType[]
  ratePlans: RatePlan[]
  selectedRatePlanId: number
  rates: Record<string, RateData>
  allRoomTypeRates: Record<string, RateData>
  roomOccupancy?: Record<string, RoomOccupancyEntry>
  daysInMonth: DayInfo[]
  adrData: Record<string, AdrDay>
  rateHistory: Record<string, RateHistoryEntry[]>
}

interface SummaryStat {
  label: string
  value: string
  color: string
  bgColor: string
  icon: LucideIcon
  title?: string
}

function OccupancySummaryBanner({ occupancyData, roomScoped, today }: { occupancyData: OccupancyDay[]; roomScoped: boolean; today: string }) {
  const futureData = occupancyData.filter((d) => d.date >= today)
  if (futureData.length === 0) return null

  const avgOcc = Math.round(futureData.reduce((s, d) => s + d.occupancyPct, 0) / futureData.length)
  const highDemandDays = futureData.filter((d) => d.occupancyPct >= 80).length
  const lowDemandDays = futureData.filter((d) => d.occupancyPct < 60).length
  // Pickup is shown in ROOMS (docs/revenue-vocabulary.md § Plain-language). Sum the booked-room
  // deltas across the forward days — rooms are additive and meaningful; occupancy points are not.
  const totalPickupRooms = futureData.reduce((s, d) => s + (d.pickup7dRooms ?? 0), 0)

  // Pickup is only tracked property-wide — room-scoped entries carry pickup7dRooms:
  // null, which would sum to a misleading "no change". Render an explicit em-dash
  // (with an explanatory title) instead of a zero when a room type is selected.
  const pickupStat: SummaryStat = roomScoped
    ? {
        label: '7d Pickup',
        value: '—',
        color: 'text-slate-500',
        bgColor: 'bg-slate-50',
        icon: Users,
        title: 'Pickup is tracked at the property level only',
      }
    : {
        label: '7d Pickup',
        value: formatPickupRooms(totalPickupRooms),
        color: totalPickupRooms > 0 ? 'text-emerald-700' : totalPickupRooms < 0 ? 'text-rose-700' : 'text-slate-600',
        bgColor: totalPickupRooms > 0 ? 'bg-emerald-50' : totalPickupRooms < 0 ? 'bg-rose-50' : 'bg-slate-50',
        icon: Users,
      }

  const stats: SummaryStat[] = [
    {
      label: 'Avg Occupancy',
      value: `${avgOcc}%`,
      color: avgOcc >= 80 ? 'text-emerald-700' : avgOcc >= 60 ? 'text-amber-700' : 'text-slate-600',
      bgColor: avgOcc >= 80 ? 'bg-emerald-50' : avgOcc >= 60 ? 'bg-amber-50' : 'bg-slate-50',
      icon: BarChart3,
    },
    {
      label: 'High Demand',
      value: `${highDemandDays}d`,
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      icon: TrendingUp,
    },
    {
      label: 'Low Demand',
      value: `${lowDemandDays}d`,
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
      icon: TrendingDown,
    },
    pickupStat,
  ]

  return (
    <div className="grid grid-cols-4 gap-3">
      {stats.map((stat) => (
        <div key={stat.label} title={stat.title} className={cn('rounded-xl border border-border/60 px-4 py-3 shadow-sm', stat.bgColor)}>
          <div className="flex items-center gap-2">
            <stat.icon className={cn('h-4 w-4', stat.color)} />
            <span className="text-xs font-medium text-muted-foreground">{stat.label}</span>
          </div>
          <div className={cn('mt-1 text-xl font-bold tabular-nums', stat.color)}>
            {stat.value}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function Occupancy({
  currentUser,
  property,
  properties,
  month,
  year,
  totalRooms,
  pooledOccupancyLabel = null,
  adrLabel = null,
  occupancyData,
  roomTypes,
  ratePlans,
  rates,
  allRoomTypeRates,
  roomOccupancy,
  daysInMonth,
  adrData,
  rateHistory,
  reportingScope,
  pageIdentity,
}: OccupancyProps) {
  const allRoomTypeIds = useMemo(() => roomTypes.map((rt) => rt.id), [roomTypes])
  // At the lead (and an ungrouped hotel) the rows are the building's rooms, named by the server so no
  // row needs a hotel label (owner ruling 2026-10-26). A following listing's Occupancy calendar keeps
  // the unit's rows it always had, a twin beside its Same rooms, so there they still wear their hotel.
  const followingListing = isDeferredScope(reportingScope)
  const showHotels = useMemo(() => followingListing && spansHotels(roomTypes), [followingListing, roomTypes])

  const {
    view,
    calendarRoomType,
    navigate,
  } = useCalendarNav({
    basePath: property.basePath,
    tab: 'occupancy',
    month,
    year,
    allRoomTypeIds,
  })

  // null = all property, number = specific room type
  const selectedRoomTypeId = calendarRoomType ?? null

  const [dayDetailDate, setDayDetailDate] = useState<string | null>(null)
  const [dayDetailRoomTypeId, setDayDetailRoomTypeId] = useState<number | null>(null)

  // Property-wide occupancy map
  const propertyOccupancyByDate = useMemo(() => {
    const map: Record<string, OccupancyDay> = {}
    for (const d of occupancyData) map[d.date] = d
    return map
  }, [occupancyData])

  // Hover popup uses totalRooms as denominator to match the day-modal "Occupancy" insight.
  // Recompute pct from booked/totalRooms so the percentage matches the fraction (e.g. "7% 1/14", not "100% 1/14").
  const propertyOccupancyForPopup = useMemo(() => {
    const map: Record<string, { booked: number; available: number; occupancyPct: number; label?: string | null }> = {}
    for (const d of occupancyData) {
      const pct = totalRooms > 0 ? Math.round((d.booked / totalRooms) * 100) : 0
      map[d.date] = { booked: d.booked, available: totalRooms, occupancyPct: pct, label: pooledOccupancyLabel }
    }
    return map
  }, [occupancyData, totalRooms, pooledOccupancyLabel])

  // Room-type-specific occupancy map (when a room type is selected)
  const roomTypeOccupancyByDate = useMemo(() => {
    if (!selectedRoomTypeId || !roomOccupancy) return null
    const map: Record<string, OccupancyDay> = {}
    for (const day of daysInMonth) {
      const key = `${selectedRoomTypeId}:${day.date}`
      const ro = roomOccupancy[key]
      if (ro) {
        const pct = ro.occupancyPct ?? 0
        map[day.date] = { date: day.date, booked: ro.booked, available: ro.total, occupancyPct: pct, pickup7d: null, pickup30d: null, pickup60d: null, pickup7dRooms: null, pickup30dRooms: null, pickup60dRooms: null, paceRatio: null, baselineOccPct: null }
      }
    }
    return map
  }, [selectedRoomTypeId, roomOccupancy, daysInMonth])

  // Active occupancy — filtered by room type or property-wide
  const occupancyByDate = roomTypeOccupancyByDate ?? propertyOccupancyByDate

  const filteredOccupancyData = useMemo(() => {
    if (!selectedRoomTypeId) return occupancyData
    return daysInMonth
      .map((day) => occupancyByDate[day.date])
      .filter(Boolean) as OccupancyDay[]
  }, [selectedRoomTypeId, occupancyData, daysInMonth, occupancyByDate])

  // Rate lookup for hover cards — scoped to selected room type or first available
  const rateByDate = useMemo(() => {
    const source = allRoomTypeRates || rates
    if (!source || !roomTypes?.length) return {}
    const map: Record<string, RateData> = {}
    const targetRoomTypes = selectedRoomTypeId
      ? roomTypes.filter((rt) => rt.id === selectedRoomTypeId)
      : roomTypes
    for (const day of daysInMonth) {
      for (const rt of targetRoomTypes) {
        const key = `${rt.id}:${day.date}`
        if (source[key]) {
          map[day.date] = source[key]
          break
        }
      }
    }
    return map
  }, [allRoomTypeRates, rates, roomTypes, daysInMonth, selectedRoomTypeId])

  // Room type filter options: "All Property" pinned first, then the
  // parent/child room-type tree (separated by a hairline divider).
  const rowMeta = useMemo(() => buildRoomTypeRowMeta(roomTypes), [roomTypes])

  const roomTypeFilterOptions = useMemo(
    () => [
      // The pooled option. At a group it is the UNIT, not "this property" — the figure behind it
      // sums every hotel's rooms, so calling it "All Property" would name the wrong subject. At the
      // lead it is the building, in the server's word for it ("Building", the pooled chip's label).
      {
        value: 'all',
        label: showHotels && reportingScope
          ? `All ${groupHotelCount(reportingScope)} listings`
          : (!followingListing && pooledOccupancyLabel) || 'All Property',
      },
      ...roomTypes.map((rt, idx) => ({
        value: rt.id.toString(),
        label: rt.name,
        ...rowMeta[rt.id],
        ...roomTypeHotelExtras(rt, rowMeta[rt.id], {
          showHotels,
          leadPropertyId: reportingScope?.leadPropertyId ?? null,
        }),
        spacedAbove: idx === 0 ? false : rowMeta[rt.id]?.spacedAbove,
        dividerBefore: idx === 0,
      })),
    ],
    [roomTypes, rowMeta, showHotels, reportingScope]
  )

  const handleViewChange = useCallback((mode: ViewMode) => {
    navigate({ view: mode })
  }, [navigate])

  const handleMonthNavigate = useCallback((m: number, y: number) => {
    navigate({ month: m, year: y })
  }, [navigate])

  const handleRoomTypeFilterChange = useCallback((v: string) => {
    navigate({ calendarRoomType: v === 'all' ? null : Number(v) })
  }, [navigate])

  // THE clock for this page — the PROPERTY's, not the viewer's. See
  // calendar-utils#todayIso for why this is not `new Date()`.
  const propertyToday = todayIso(property.timezone)

  const handleCellClick = useCallback((date: string, roomTypeId?: number) => {
    setDayDetailDate(date)
    setDayDetailRoomTypeId(roomTypeId ?? selectedRoomTypeId ?? null)
  }, [selectedRoomTypeId])

  const basePath = `${property.basePath}/calendar`
  const selectedRoomType = selectedRoomTypeId
    ? roomTypes.find((rt) => rt.id === selectedRoomTypeId)
    : undefined

  const figureScope = useMemo(
    () => ({ pooledLabel: pooledOccupancyLabel, adrLabel }),
    [pooledOccupancyLabel, adrLabel],
  )

  return (
    <OperatorLayout title="Occupancy" currentUser={currentUser} property={property} properties={properties}>
      {/* What the pooled figures cover, for the hover card, the day details and the month strip. */}
      <FigureScopeContext.Provider value={figureScope}>
      <div className="space-y-5">
        {/* Page header with tabs and view switcher */}
        <div className="animate-slide-in flex items-center justify-between" style={{ animationDelay: '0ms' }}>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-foreground">Rate Calendar</h1>
              <HelpTooltip
                content="Monitor booking demand. Green occupancy = at or above target. Pickup shows recent booking gains. Click any cell to see full details."
                learnMore="/guides/rate-calendar"
              />
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {/* `totalRooms` is the UNIT's capacity — the same denominator the engine prices
                  against — so it must be attributed to the unit, never to the hotel in the header. */}
              Monthly occupancy overview for{' '}
              {(showHotels && reportingScope?.groupName) || property.name} ({totalRooms} total rooms)
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Tab navigation (Prices / Occupancy) */}
            <nav className="inline-flex h-9 items-center rounded-md bg-muted p-1 text-muted-foreground">
              <Link
                href={`${basePath}/prices?month=${month}&year=${year}${view !== 'calendar' ? `&view=${view}` : ''}`}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1 text-sm font-medium transition-all text-muted-foreground hover:text-foreground"
              >
                Prices
              </Link>
              <Link
                href={`${basePath}/occupancy?month=${month}&year=${year}${view !== 'calendar' ? `&view=${view}` : ''}`}
                className={cn(
                  'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1 text-sm font-medium transition-all',
                  'bg-background text-foreground shadow-sm'
                )}
              >
                Occupancy
              </Link>
            </nav>

            <ViewSwitcher value={view} onChange={handleViewChange} />
          </div>
        </div>

        {/* Occupancy summary banner */}
        <CalendarUpdateBanner />

        {/* At a LEAD the pooled strip carries no caption — the room filter names its hotels, which
            is where the reach is legible. At a MEMBER it still has to be said: the room filter
            spans the unit here too, so a per-room figure is the figure for THAT hotel's room — an
            empty member's room reads 0% beside a 69%-full unit, deliberately. */}
        <DeferredScopeNote
          scope={reportingScope}
          pooled="Occupancy, capacity, pickup and pace"
          own="a per-room figure is that room’s own listing’s"
          leadSuffix="/calendar/occupancy"
          className="animate-slide-in"
        />

        <div className="animate-slide-in" style={{ animationDelay: '50ms' }}>
          <OccupancySummaryBanner occupancyData={filteredOccupancyData} roomScoped={selectedRoomTypeId != null} today={propertyToday} />
        </div>

        {/* Month picker + room type filter */}
        <div className="animate-slide-in" style={{ animationDelay: '80ms' }}>
          <MonthPicker month={month} year={year} onNavigate={handleMonthNavigate}>
            <SingleSelectDropdown
              label="Room Type"
              options={roomTypeFilterOptions}
              value={selectedRoomTypeId ? selectedRoomTypeId.toString() : 'all'}
              onChange={handleRoomTypeFilterChange}
            />
          </MonthPicker>
        </div>

        {/* Legend */}
        <div className="animate-slide-in flex flex-wrap items-center gap-3 text-xs" style={{ animationDelay: '100ms' }}>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm bg-emerald-50 border border-emerald-200" />
            <span className="text-muted-foreground">80%+ occupancy</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm bg-amber-50 border border-amber-200" />
            <span className="text-muted-foreground">60-79%</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm bg-slate-50 border border-slate-200" />
            <span className="text-muted-foreground">Below 60%</span>
          </span>
        </div>

        {/* View content */}
        <div className="animate-slide-in" style={{ animationDelay: '120ms' }}>
          {view === 'calendar' && (
            <OccupancyCalendarGrid
              year={year}
              month={month}
              occupancyByDate={occupancyByDate}
              propertyOccupancyByDate={propertyOccupancyForPopup}
              currency={property.currency}
              rateByDate={rateByDate}
              roomType={selectedRoomType}
              onCellClick={handleCellClick}
              adrData={adrData}
              today={propertyToday}
            />
          )}

          {view === 'grid' && (
            <OccupancyGrid
              daysInMonth={daysInMonth}
              occupancyData={filteredOccupancyData}
              propertyOccupancyByDate={propertyOccupancyForPopup}
              currency={property.currency}
              rateByDate={rateByDate}
              onCellClick={handleCellClick}
              adrData={adrData}
              today={propertyToday}
            />
          )}

          {view === 'timeline' && (
            <OccupancyTimelineGrid
              today={propertyToday}
              daysInMonth={daysInMonth}
              occupancyByDate={occupancyByDate}
              propertyOccupancyByDate={propertyOccupancyForPopup}
              currency={property.currency}
              rateByDate={rateByDate}
              onCellClick={handleCellClick}
              adrData={adrData}
            />
          )}
        </div>
      </div>

      <DayDetailModal
        open={dayDetailDate != null}
        onOpenChange={(open) => { if (!open) setDayDetailDate(null) }}
        selectedDate={dayDetailDate ?? ''}
        selectedRoomTypeId={dayDetailRoomTypeId}
        daysInMonth={daysInMonth}
        roomTypes={roomTypes}
        // Occupancy's hover cards read `rateByDate`, sourced from `allRoomTypeRates`
        // (`allRoomTypeRates || rates`, and allRoomTypeRates is always present).
        // Passing that same display map guarantees hover == modal — merging the
        // plan-scoped `rates` over it could overlay a different plan and diverge.
        rates={allRoomTypeRates}
        occupancyData={occupancyData}
        roomOccupancy={roomOccupancy}
        totalRooms={totalRooms}
        currency={property.currency}
        propertyBasePath={property.basePath}
        today={propertyToday}
        adrData={adrData}
        rateHistory={rateHistory}
        reportingScope={reportingScope}
        pageIdentity={pageIdentity}
      />
      </FigureScopeContext.Provider>
    </OperatorLayout>
  )
}
